import * as React from "react";
import { StackLayout } from "@progress/kendo-react-layout";
import CPModal from "shared/components/shared/CPModal/CPModal";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPEmailTextBox from "shared/components/shared/CPEmailTextBox/CPEmailTextBox";
import CPCheckbox from "shared/components/shared/CPCheckbox/CPCheckbox";
import { CPText } from "shared/components/base";
import {
  User,
  UserEmailAlert,
  useUserEmailAlertQuery,
  useUpsertUserEmailAlertMutation,
} from "data/user/user";
import useAppStore from "shared/state/useAppState";

interface AlertSettingsModalProps {
  open: boolean;
  onClose: () => void;
  user: User | null | undefined;
}

interface FormState {
  email: string;
  isActive: boolean;
}

const AlertSettingsModal: React.FC<AlertSettingsModalProps> = ({
  open,
  onClose,
  user,
}) => {
  const { data: alertData, isLoading } = useUserEmailAlertQuery();
  const mutation = useUpsertUserEmailAlertMutation();
  const { setToastMsg } = useAppStore();

  const [form, setForm] = React.useState<FormState>({
    email: "",
    isActive: false,
  });
  const [emailError, setEmailError] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (!open) return;
    if (alertData) {
      setForm({
        email: alertData.email ?? user?.email ?? "",
        isActive: alertData.isActive,
      });
    } else if (user) {
      setForm({ email: user.email ?? "", isActive: false });
    }
    setEmailError(undefined);
  }, [open, alertData, user]);

  const handleSave = async () => {
    if (emailError) return;

    const payload: UserEmailAlert = {
      email: form.email || null,
      isActive: form.isActive,
    };

    try {
      await mutation.mutateAsync(payload);
      setToastMsg("Alert settings saved.", "success");
      onClose();
    } catch {
      setToastMsg("Failed to save alert settings.", "error");
    }
  };

  return (
    <CPModal
      title="Alert Settings"
      open={open}
      onClose={onClose}
      width={420}
      actionBtns={
        <>
          <CPButton fillMode="outline" onClick={onClose}>
            Cancel
          </CPButton>
          <CPButton
            themeColor="primary"
            showLoader={mutation.isLoading}
            onClick={handleSave}
          >
            Save
          </CPButton>
        </>
      }
    >
      {isLoading ? (
        <CPText>Loading…</CPText>
      ) : (
        <StackLayout orientation="vertical" gap={16}>
          <CPText font="montserrat" style={{ fontWeight: 600, fontSize: 13 }}>
            Choose which events you want to be notified about and where
            notifications should be sent.
          </CPText>

          <CPCheckbox
            name="isActive"
            label="Alert me when a new pending access request is submitted"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.value }))}
          />

          <CPEmailTextBox
            name="email"
            label="Send alerts to"
            value={form.email}
            onChange={(e) => {
              setForm((f) => ({ ...f, email: e.value as string }));
              setEmailError(e.isValid ? undefined : e.error);
            }}
            errorMsg={emailError}
          />

          <CPText textStyle="caption" color="secondary">
            Leave the email blank to use your account email ({user?.email}).
          </CPText>
        </StackLayout>
      )}
    </CPModal>
  );
};

export default AlertSettingsModal;
